/**
 * UnifiedChildList - Unified drag-drop list for attachments and sub-items
 *
 * Merges attachments and sub-items into a single sortable list.
 * Pending items appear at the end.
 */

import { useEffect, useMemo, useCallback } from 'react';
import {
  Trash2,
  Edit2,
  Check,
  CheckSquare,
  DollarSign,
  FileText,
  Calendar,
  Paperclip,
} from 'lucide-react';
import { DraggableList, FileDropZone } from '@/components/ui';
import { useItemStore, useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { AttachmentTile, PendingAttachmentTile } from './AttachmentTile';
import type {
  SubItem,
  ItemType,
  Attachment,
  TaskSubItem,
  TransactionSubItem,
  NoteSubItem,
  KeyDateSubItem,
} from '@perfin/shared';
import { formatCurrency, formatRelativeDate, isCompleteStatus, getDefaultStatus, getCompleteStatus } from '@perfin/shared';
import type { PendingSubItem, CachedPendingFile } from '@/services';

// ============================================================================
// TYPES
// ============================================================================

interface UnifiedChildListProps {
  itemId: string;
  itemCreatedBy?: string;  // createdBy of parent item (for Firestore path)
  attachments: Attachment[];
  pendingAttachments: CachedPendingFile[];
  pendingSubItems: PendingSubItem[];
  /** Deferred reorder - if set, use this order for saved items instead of Firestore order */
  reorderedChildren?: Array<{ type: 'attachment' | 'subItem'; id: string }>;
  showFileDropZone: boolean;
  onFilesDrop: (files: File[]) => void;
  onRemoveExistingAttachment: (attachment: Attachment) => void;
  onRemovePendingAttachment: (index: number) => void;
  onRemovePendingSubItem: (tempId: string) => void;
  onReorderPendingItems?: (pendingAttachments: CachedPendingFile[], pendingSubItemIds: string[]) => void;
  /** Called when saved items are reordered - defers to Save */
  onReorderSavedItems?: (reorderedChildren: Array<{ type: 'attachment' | 'subItem'; id: string }>) => void;
  onEditSubItem?: (subItem: SubItem) => void;
  onEditPendingSubItem?: (pending: PendingSubItem) => void;
  disabled?: boolean;
}

// Unified item type for the list
type UnifiedChildItem =
  | { kind: 'attachment'; id: string; order: number; data: Attachment }
  | { kind: 'subItem'; id: string; order: number; data: SubItem }
  | { kind: 'pendingAttachment'; id: string; order: number; addedAt: number; file: File; index: number }
  | { kind: 'pendingSubItem'; id: string; order: number; addedAt: number; data: PendingSubItem };

// ============================================================================
// CONSTANTS
// ============================================================================

const TYPE_ICONS: Record<ItemType, React.ElementType> = {
  task: CheckSquare,
  transaction: DollarSign,
  note: FileText,
  keyDate: Calendar,
};

const TYPE_ICON_COLORS: Record<ItemType, string> = {
  task: 'text-blue-500',
  transaction: 'text-green-500',
  note: 'text-yellow-500',
  keyDate: 'text-purple-500',
};

// ============================================================================
// SUB-ITEM ROW COMPONENT
// ============================================================================

interface SubItemRowProps {
  subItem: SubItem;
  onEdit: (subItem: SubItem) => void;
  onDelete: (subItem: SubItem) => void;
  onToggleStatus: (subItem: SubItem) => void;
  disabled?: boolean;
}

function SubItemRow({
  subItem,
  onEdit,
  onDelete,
  onToggleStatus,
  disabled = false,
}: SubItemRowProps) {
  const Icon = TYPE_ICONS[subItem.type];
  const status = (subItem as unknown as { status?: string }).status;
  const isComplete = isCompleteStatus(status);
  const hasAttachments = (subItem.attachments?.length ?? 0) > 0;

  const secondaryInfo = (() => {
    switch (subItem.type) {
      case 'task':
        const task = subItem as TaskSubItem;
        return task.dueDate ? formatRelativeDate(task.dueDate) : null;
      case 'transaction':
        const trans = subItem as TransactionSubItem;
        return formatCurrency(trans.amount, trans.currency);
      case 'note':
        const note = subItem as NoteSubItem;
        return note.content ? note.content.slice(0, 50) + (note.content.length > 50 ? '...' : '') : null;
      case 'keyDate':
        const kd = subItem as KeyDateSubItem;
        return kd.dueDate ? formatRelativeDate(kd.dueDate) : null;
    }
  })();

  return (
    <div
      onClick={() => onEdit(subItem)}
      className={`
        flex items-center gap-3 rounded-lg cursor-pointer hover:bg-gray-50
        ${isComplete ? 'opacity-60' : ''}
      `}
    >
      {/* Type Icon - non-interactive */}
      <Icon className={`h-5 w-5 ${TYPE_ICON_COLORS[subItem.type] || 'text-gray-500'}`} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`font-medium truncate ${isComplete ? 'line-through text-green-600' : 'text-gray-900'}`}>
          {subItem.name}
        </p>
        {secondaryInfo && (
          <p className="text-sm text-gray-500 truncate">{secondaryInfo}</p>
        )}
      </div>

      {/* Actions */}
      {!disabled && (
        <div className="flex items-center gap-1">
          {/* Attachment indicator */}
          {hasAttachments && (
            <span
              className="p-1 text-gray-400"
              title={`${subItem.attachments!.length} attachment(s)`}
            >
              <Paperclip className="h-4 w-4" />
            </span>
          )}
          {/* Completion toggle */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleStatus(subItem); }}
            className="p-1 rounded hover:bg-gray-100"
            title={isComplete ? 'Mark incomplete' : 'Mark complete'}
          >
            <Check
              className={`h-4 w-4 ${isComplete ? 'text-green-500' : 'text-gray-400'}`}
              strokeWidth={3}
            />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(subItem); }}
            className="p-1 text-gray-400 hover:text-red-600 rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PENDING SUB-ITEM ROW COMPONENT
// ============================================================================

interface PendingSubItemRowProps {
  pending: PendingSubItem;
  onEdit?: (pending: PendingSubItem) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function PendingSubItemRow({ pending, onEdit, onRemove, disabled = false }: PendingSubItemRowProps) {
  const Icon = TYPE_ICONS[pending.input.type];

  const getSecondaryInfo = () => {
    switch (pending.input.type) {
      case 'transaction':
        return formatCurrency(pending.input.amount || 0, pending.input.currency || 'USD');
      case 'note':
        return pending.input.content ? pending.input.content.slice(0, 50) + (pending.input.content.length > 50 ? '...' : '') : null;
      default:
        return pending.input.description ? pending.input.description.slice(0, 50) : null;
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-lg">
      <Icon className="h-5 w-5 text-blue-600" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate text-gray-900">{pending.input.name}</p>
          <span className="text-xs text-blue-600 font-medium">(pending)</span>
        </div>
        {getSecondaryInfo() && (
          <p className="text-sm text-gray-500 truncate">{getSecondaryInfo()}</p>
        )}
      </div>

      {!disabled && (
        <div className="flex items-center gap-1">
          {onEdit && (
            <button
              onClick={() => onEdit(pending)}
              className="p-1 text-gray-400 hover:text-blue-600 rounded"
              title="Edit"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onRemove}
            className="p-1 text-gray-400 hover:text-red-600 rounded"
            title="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function UnifiedChildList({
  itemId,
  itemCreatedBy,
  attachments,
  pendingAttachments,
  pendingSubItems,
  reorderedChildren,
  showFileDropZone,
  onFilesDrop,
  onRemoveExistingAttachment,
  onRemovePendingAttachment,
  onRemovePendingSubItem,
  onReorderPendingItems,
  onReorderSavedItems,
  onEditSubItem,
  onEditPendingSubItem,
  disabled = false,
}: UnifiedChildListProps) {
  const { user } = useAuthStore();
  const { subItemsMap, subscribeToItemSubItems, removeSubItem, editSubItem } = useItemStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  // Subscribe to sub-items
  // Use item's createdBy for Firestore path, fallback to user.uid
  const creatorId = itemCreatedBy || user?.uid || '';
  useEffect(() => {
    if (!user || !itemId || !creatorId) return;
    const unsubscribe = subscribeToItemSubItems(creatorId, itemId);
    return unsubscribe;
  }, [user, itemId, creatorId, subscribeToItemSubItems]);

  const currentSubItems = subItemsMap[itemId] || [];

  // Build unified list
  const unifiedItems = useMemo<UnifiedChildItem[]>(() => {
    const items: UnifiedChildItem[] = [];

    // If we have a deferred reorder, use that order for saved items
    if (reorderedChildren && reorderedChildren.length > 0) {
      // Build items in the deferred order
      reorderedChildren.forEach((entry, idx) => {
        if (entry.type === 'attachment') {
          const attachment = attachments.find(a => a.id === entry.id);
          if (attachment) {
            items.push({
              kind: 'attachment',
              id: `attachment-${attachment.id}`,
              order: idx,
              data: attachment,
            });
          }
        } else {
          const subItem = currentSubItems.find(s => s.id === entry.id);
          if (subItem) {
            items.push({
              kind: 'subItem',
              id: `subitem-${subItem.id}`,
              order: idx,
              data: subItem,
            });
          }
        }
      });
    } else {
      // Use Firestore order
      // Add saved attachments
      attachments.forEach((attachment) => {
        items.push({
          kind: 'attachment',
          id: `attachment-${attachment.id}`,
          order: attachment.order ?? 999,
          data: attachment,
        });
      });

      // Add saved sub-items
      currentSubItems.forEach((subItem) => {
        items.push({
          kind: 'subItem',
          id: `subitem-${subItem.id}`,
          order: subItem.order ?? 999,
          data: subItem,
        });
      });

      // Sort saved items by order
      items.sort((a, b) => a.order - b.order);
    }

    // Calculate next order for pending items
    const maxOrder = items.length > 0 ? Math.max(...items.map((i) => i.order)) : -1;

    // Build pending items with timestamps for interleaved ordering
    const pendingItems: Array<
      | { kind: 'pendingAttachment'; addedAt: number; file: File; index: number }
      | { kind: 'pendingSubItem'; addedAt: number; data: PendingSubItem }
    > = [];

    // Add pending attachments with their addedAt timestamp
    pendingAttachments.forEach((cached, index) => {
      pendingItems.push({
        kind: 'pendingAttachment',
        addedAt: cached.addedAt,
        file: cached.file,
        index,
      });
    });

    // Add pending sub-items - extract timestamp from tempId (format: temp_<timestamp>_<random>)
    pendingSubItems.forEach((pending) => {
      const timestampMatch = pending.tempId.match(/^temp_(\d+)_/);
      const addedAt = timestampMatch ? parseInt(timestampMatch[1], 10) : Date.now();
      pendingItems.push({
        kind: 'pendingSubItem',
        addedAt,
        data: pending,
      });
    });

    // Sort pending items by addedAt to preserve interleaved order
    pendingItems.sort((a, b) => a.addedAt - b.addedAt);

    // Append sorted pending items with incremental order
    pendingItems.forEach((pending, idx) => {
      if (pending.kind === 'pendingAttachment') {
        items.push({
          kind: 'pendingAttachment',
          id: `pending-attachment-${pending.index}`,
          order: maxOrder + 1 + idx,
          addedAt: pending.addedAt,
          file: pending.file,
          index: pending.index,
        });
      } else {
        items.push({
          kind: 'pendingSubItem',
          id: `pending-subitem-${pending.data.tempId}`,
          order: maxOrder + 1 + idx,
          addedAt: pending.addedAt,
          data: pending.data,
        });
      }
    });

    return items;
  }, [attachments, currentSubItems, pendingAttachments, pendingSubItems, reorderedChildren]);

  // Handle reorder
  const handleReorder = useCallback((reorderedItems: UnifiedChildItem[]) => {
    if (!user || disabled) return;

    // Extract saved items for deferred Firestore update
    const savedItems = reorderedItems
      .filter((item) => item.kind === 'attachment' || item.kind === 'subItem')
      .map((item) => ({
        type: item.kind === 'attachment' ? 'attachment' as const : 'subItem' as const,
        id: item.kind === 'attachment'
          ? (item.data as Attachment).id
          : (item.data as SubItem).id,
      }));

    // Extract pending items in new order
    const reorderedPendingAttachments: CachedPendingFile[] = [];
    const reorderedPendingSubItemIds: string[] = [];

    for (const item of reorderedItems) {
      if (item.kind === 'pendingAttachment') {
        reorderedPendingAttachments.push({
          file: item.file,
          addedAt: item.addedAt,
        });
      } else if (item.kind === 'pendingSubItem') {
        reorderedPendingSubItemIds.push(item.data.tempId);
      }
    }

    // Update pending items order via callback
    if (onReorderPendingItems) {
      onReorderPendingItems(reorderedPendingAttachments, reorderedPendingSubItemIds);
    }

    // Defer saved items reorder to Save (via draft)
    if (savedItems.length > 0 && onReorderSavedItems) {
      onReorderSavedItems(savedItems);
    }
  }, [user, disabled, onReorderPendingItems, onReorderSavedItems]);

  // Sub-item handlers (creatorId defined above for subscription)
  const handleDeleteSubItem = useCallback((subItem: SubItem) => {
    if (!user) return;

    confirmDelete({
      title: 'Delete Sub-item',
      message: `Are you sure you want to delete "${subItem.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await removeSubItem(creatorId, itemId, subItem.id);
          toast.success('Sub-item deleted');
        } catch {
          toast.error('Failed to delete sub-item');
        }
      },
    });
  }, [user, itemId, creatorId, removeSubItem, toast, confirmDelete]);

  const handleToggleStatus = useCallback(async (subItem: SubItem) => {
    if (!user) return;

    const currentStatus = (subItem as unknown as { status?: string }).status;
    const lastKnownStatus = (subItem as unknown as { lastKnownStatus?: string }).lastKnownStatus;
    const isComplete = isCompleteStatus(currentStatus);

    try {
      if (isComplete) {
        // Restore previous status from lastKnownStatus, or default
        const restoredStatus = lastKnownStatus || getDefaultStatus(subItem.type) || 'draft';
        await editSubItem(creatorId, itemId, subItem.id, {
          status: restoredStatus as 'new' | 'inProgress' | 'complete' | 'draft' | 'pending' | 'final',
          lastKnownStatus: undefined
        });
      } else {
        // Save current status before marking complete/final
        const completeStatus = getCompleteStatus(subItem.type) as 'complete' | 'final';
        await editSubItem(creatorId, itemId, subItem.id, {
          status: completeStatus,
          lastKnownStatus: currentStatus
        });
      }
    } catch {
      toast.error('Failed to update status');
    }
  }, [user, itemId, creatorId, editSubItem, toast]);

  // Render individual items
  const renderItem = useCallback((item: UnifiedChildItem) => {
    switch (item.kind) {
      case 'attachment':
        return (
          <AttachmentTile
            attachment={item.data}
            onDelete={onRemoveExistingAttachment}
            disabled={disabled}
          />
        );
      case 'subItem':
        return (
          <SubItemRow
            subItem={item.data}
            onEdit={onEditSubItem || (() => {})}
            onDelete={handleDeleteSubItem}
            onToggleStatus={handleToggleStatus}
            disabled={disabled}
          />
        );
      case 'pendingAttachment':
        return (
          <PendingAttachmentTile
            file={item.file}
            onRemove={() => onRemovePendingAttachment(item.index)}
            disabled={disabled}
          />
        );
      case 'pendingSubItem':
        return (
          <PendingSubItemRow
            pending={item.data}
            onEdit={onEditPendingSubItem}
            onRemove={() => onRemovePendingSubItem(item.data.tempId)}
            disabled={disabled}
          />
        );
    }
  }, [
    disabled,
    onRemoveExistingAttachment,
    onRemovePendingAttachment,
    onRemovePendingSubItem,
    onEditSubItem,
    onEditPendingSubItem,
    handleDeleteSubItem,
    handleToggleStatus,
  ]);

  const totalCount = unifiedItems.length;

  return (
    <div className="space-y-4">
      {/* File Drop Zone - shown when toggled */}
      {showFileDropZone && !disabled && (
        <FileDropZone
          onDrop={onFilesDrop}
          accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
          multiple
          className="mb-4"
        />
      )}

      {/* Empty state */}
      {totalCount === 0 && !showFileDropZone && (
        <div className="text-center py-16 text-gray-500">
          <p className="text-sm">No attachments or sub-items yet</p>
          <p className="text-xs mt-1">Use the Add button above to add content</p>
        </div>
      )}

      {/* Unified Draggable List */}
      {totalCount > 0 && (
        <DraggableList
          items={unifiedItems}
          onReorder={handleReorder}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
        />
      )}
    </div>
  );
}
